<script setup>
import {
  ref,
  computed,
  onBeforeUnmount,
  onMounted,
  nextTick,
  watch,
} from 'vue';
import {
  clampToVisualViewport,
  getVisualViewport,
  layoutPx,
  layoutToVisual,
} from 'dashboard/helper/dashboardZoom';
import { activateMenu, deactivateMenu } from './contextMenu/activeMenuRegistry';

const props = defineProps({
  count: { type: Number, required: true },
});
const emit = defineEmits(['exit', 'markUnread', 'mute', 'resolve']);

// Aceleres: so um menu aberto por vez na tela — ver activeMenuRegistry.
const selectionMenuOwner = Symbol('acl-selection-toolbar-menu');
const menuOpen = ref(false);
const rootRef = ref(null);
const kebabRef = ref(null);
const menuRef = ref(null);
const menuPosition = ref({ top: '0px', left: '0px' });

const label = computed(() => `Selecionados: ${props.count}`);

const positionMenu = () => {
  if (!kebabRef.value) return;

  if (!window.matchMedia('(max-width: 767px)').matches) {
    menuPosition.value = {
      top: '105.3px',
      left: '480.4px',
    };
    return;
  }

  const viewport = getVisualViewport();
  const buttonRect = kebabRef.value.getBoundingClientRect();
  const menuRect = menuRef.value?.getBoundingClientRect();
  const menuWidth = menuRect?.width || layoutToVisual(240, viewport.zoom);
  const menuHeight = menuRect?.height || layoutToVisual(160, viewport.zoom);
  const gap = layoutToVisual(8, viewport.zoom);
  const padding = layoutToVisual(8, viewport.zoom);
  const belowTop = buttonRect.bottom + gap;
  const aboveTop = buttonRect.top - menuHeight - gap;
  const preferredTop =
    belowTop + menuHeight <= viewport.height - padding ? belowTop : aboveTop;
  const position = clampToVisualViewport(
    buttonRect.right - menuWidth,
    preferredTop,
    menuWidth,
    menuHeight,
    padding,
    viewport
  );

  menuPosition.value = {
    top: layoutPx(position.top, viewport.zoom),
    left: layoutPx(position.left, viewport.zoom),
  };
};

const closeMenu = () => {
  menuOpen.value = false;
  deactivateMenu(selectionMenuOwner);
};
const toggleMenu = async () => {
  menuOpen.value = !menuOpen.value;
  if (menuOpen.value) {
    activateMenu({ owner: selectionMenuOwner, close: closeMenu });
    await nextTick();
    positionMenu();
  } else {
    deactivateMenu(selectionMenuOwner);
  }
};

// Desmarcar a ultima conversa tira o sanduiche do ar; o menu teleportado nao
// pode sobreviver ao botao que o abriu.
watch(
  () => props.count,
  value => {
    if (!value) closeMenu();
  }
);

const onDocClick = e => {
  if (!menuOpen.value) return;
  if (rootRef.value && rootRef.value.contains(e.target)) return;
  if (menuRef.value && menuRef.value.contains(e.target)) return;
  closeMenu();
};
const onScrollOrResize = () => {
  if (menuOpen.value) closeMenu();
};
onMounted(() => {
  document.addEventListener('click', onDocClick);
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);
});
onBeforeUnmount(() => {
  deactivateMenu(selectionMenuOwner);
  document.removeEventListener('click', onDocClick);
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize);
});

const onAction = action => {
  closeMenu();
  emit(action);
};
</script>

<template>
  <div ref="rootRef" class="acl-sel-toolbar">
    <button
      type="button"
      class="acl-sel-toolbar__btn acl-sel-toolbar__close"
      aria-label="Sair da seleção"
      @click="emit('exit')"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>

    <span class="acl-sel-toolbar__label">{{ label }}</span>

    <!-- Aceleres: o sanduiche so existe depois que ha conversa selecionada;
         com zero selecionadas nao ha acao em massa a oferecer. -->
    <div v-if="count" class="acl-sel-toolbar__menu-wrap">
      <button
        ref="kebabRef"
        type="button"
        class="acl-sel-toolbar__btn acl-sel-toolbar__kebab"
        aria-label="Ações em massa"
        @click.stop="toggleMenu"
      >
        <span class="i-lucide-more-vertical size-6" aria-hidden="true" />
      </button>
    </div>
    <Teleport to="body">
      <!-- Aceleres: mesmas utilitarias do DropdownMenu usado pelo
           .acl-conversation-more-actions-menu (cabecalho da conversa), para o
           menu de selecao ter o mesmo vidro/contorno/raio. O `absolute` do
           original nao entra: aqui o menu e teleportado e posicionado fixo. -->
      <div
        v-if="menuOpen"
        ref="menuRef"
        class="acl-sel-toolbar__menu bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container rounded-xl z-50 gap-2 flex flex-col shadow-lg pb-2 px-2 pt-2"
        :style="menuPosition"
        role="menu"
      >
        <button
          class="acl-sel-toolbar__menu-item"
          type="button"
          :disabled="!count"
          @click="onAction('markUnread')"
        >
          <span
            class="acl-sel-toolbar__menu-icon i-lucide-mail"
            aria-hidden="true"
          />
          <span class="acl-sel-toolbar__menu-label">Marcar como não lida</span>
        </button>
        <button
          class="acl-sel-toolbar__menu-item"
          type="button"
          :disabled="!count"
          @click="onAction('mute')"
        >
          <span
            class="acl-sel-toolbar__menu-icon i-lucide-bell-off"
            aria-hidden="true"
          />
          <span class="acl-sel-toolbar__menu-label">
            Silenciar notificações
          </span>
        </button>
        <button
          class="acl-sel-toolbar__menu-item"
          type="button"
          :disabled="!count"
          @click="onAction('resolve')"
        >
          <span
            class="acl-sel-toolbar__menu-icon i-lucide-check-check"
            aria-hidden="true"
          />
          <span class="acl-sel-toolbar__menu-label">Concluir conversas</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.acl-sel-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 53.33px;
  padding: 0 12px;
  background-color: #c2bdb826;
  border-bottom: 1px solid #e1e4e8;
  position: relative;
}
.acl-sel-toolbar__btn {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #1a1a1a;
  cursor: pointer;
  transition: background-color 0.15s;
}
.acl-sel-toolbar__btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
}
.acl-sel-toolbar__label {
  flex: 1;
  font-size: 15px;
  color: #1a1a1a;
  text-align: left;
}
.acl-sel-toolbar__menu-wrap {
  position: relative;
}
/* Aceleres: o menu espelha o .acl-conversation-more-actions-menu — o fundo, o
   contorno e o raio vem das utilitarias no template; aqui ficam so a posicao
   (fixa, porque o menu e teleportado) e a largura. */
.acl-sel-toolbar__menu {
  position: fixed;
  /* A largura acompanha o rotulo mais longo — os itens tem `white-space:
     nowrap`, entao `max-content` nao quebra linha. */
  width: max-content;
  min-width: 0;
  max-width: calc(100vw - 24px);
  /* O `gap-2` das utilitarias abria 0.5rem entre as linhas; os itens ja tem
     recuo proprio. */
  gap: 0;
}
/* Item no mesmo desenho do botao do DropdownMenu, com as medidas do
   .acl-header-menu-item (gap 8px, recuo 9px 14px, altura pelo conteudo). */
.acl-sel-toolbar__menu-item {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  gap: 8px;
  padding: 9px 14px;
  background: transparent;
  border: 0;
  border-radius: 0.5rem;
  text-align: left;
  color: rgb(var(--slate-12));
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}
.acl-sel-toolbar__menu-label {
  min-width: 0;
  /* Mesma medida do .acl-header-menu-item. */
  font-size: 13px;
  line-height: 1.25rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acl-sel-toolbar__menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  /* O font-size acompanha a caixa porque os `i-lucide-*` dimensionam a mascara
     em 1em; sem ele o glifo desenha menor que os 16px do quadro. */
  font-size: 16px;
  background-color: currentColor;
  color: inherit;
  opacity: 1;
}
.acl-sel-toolbar__menu-item:hover:not(:disabled) {
  background-color: rgba(var(--alpha-1));
}
:global(.dark) .acl-sel-toolbar__menu-item:hover:not(:disabled) {
  background-color: rgba(var(--alpha-2));
}
.acl-sel-toolbar__menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
</style>
